/**
 * /university/training/$sessionId — single session detail.
 *
 * Two-column layout on desktop: iteration timeline (left), selected
 * iteration diff vs previous (right). Founder & tuner can append a new
 * iteration (`tuner_correction`) or approve an iteration as an example
 * (which optionally bumps the skill version).
 *
 * The "append iteration" mini-form is inline at the bottom of the left
 * pane to keep the founder in the same context. Submit shows an
 * optimistic state and refetches on success.
 */
import type { TrainingIteration, TrainingIterationKind } from '@chaos/types/learning';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Check, ChevronLeft, Loader2, Plus, Send } from 'lucide-react';
import * as React from 'react';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { useToaster } from '../../../../components/ui/toast';
import { DraftDiffViewer } from '../../../../components/university/draft-diff-viewer';
import {
  fmtRelative,
  SESSION_STATUS_LABEL,
  sessionStatusTone,
  TONE_DOT_CLASS,
} from '../../../../components/university/formatters';
import {
  IterationsTimeline,
  KIND_LABEL,
} from '../../../../components/university/iterations-timeline';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

function TrainingSessionDetailRoute() {
  const { sessionId } = Route.useParams();
  const qc = useQueryClient();
  const { toast } = useToaster();

  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canWrite = role === 'founder' || role === 'admin' || role === 'tuner';

  const detailQ = useQuery({
    queryKey: learningKeys.trainingSession(sessionId),
    queryFn: () => learningApi.getTrainingSession(sessionId),
    refetchInterval: 30_000,
  });

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [newIterationKind, setNewIterationKind] =
    React.useState<TrainingIterationKind>('tuner_correction');
  const [newContent, setNewContent] = React.useState('');
  const [dryRun, setDryRun] = React.useState(false);

  // Default the selection to the latest iteration when the data loads.
  React.useEffect(() => {
    if (detailQ.data && selectedId === null) {
      const last = detailQ.data.iterations[detailQ.data.iterations.length - 1];
      if (last) setSelectedId(last.id);
    }
  }, [detailQ.data, selectedId]);

  const appendM = useMutation({
    mutationFn: () =>
      learningApi.appendIteration(sessionId, {
        sessionId,
        kind: newIterationKind,
        content: newContent,
        dryRun,
      }),
    onSuccess: (data) => {
      qc.setQueryData(learningKeys.trainingSession(sessionId), data);
      qc.invalidateQueries({ queryKey: learningKeys.trainingSessions });
      const last = data.iterations[data.iterations.length - 1];
      if (last) setSelectedId(last.id);
      setNewContent('');
      toast({ title: 'Iteration appended' });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not append iteration',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const approveM = useMutation({
    mutationFn: (iterationId: string) =>
      learningApi.approveExample(sessionId, {
        sessionId,
        iterationId,
        markedAs: 'positive',
        bumpSkillVersion: true,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: learningKeys.trainingSession(sessionId) });
      qc.invalidateQueries({ queryKey: learningKeys.skills });
      toast({
        title: 'Example approved',
        description: `Skill version: ${data.previousVersion} → ${data.newVersion}`,
        variant: 'success',
      });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not approve',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  if (detailQ.isPending) {
    return <div className="p-6 text-[12px] text-muted-foreground">Loading session…</div>;
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="p-6">
        <Link
          to="/university/training"
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Training
        </Link>
        <div className="mt-3 rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          {(detailQ.error as Error)?.message ?? 'Session not found.'}
        </div>
      </div>
    );
  }

  const { session, iterations } = detailQ.data;
  const selected = iterations.find((it) => it.id === selectedId) ?? null;
  const prev = selected ? findPreviousIteration(iterations, selected) : null;
  const tone = sessionStatusTone(session.status);
  const statusLabel = SESSION_STATUS_LABEL[session.status] ?? session.status;

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-2">
        <Link
          to="/university/training"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Training
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
              />
              <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
                {session.skillName ?? 'Untitled session'}
              </h1>
              <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {statusLabel}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              {session.skillKey ? <span className="font-mono">{session.skillKey}</span> : null}
              <span>started {fmtRelative(session.startedAt)}</span>
              {session.endedAt ? <span>· ended {fmtRelative(session.endedAt)}</span> : null}
              {session.tunerName ? <span>· tuner {session.tunerName}</span> : null}
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div className="flex flex-col gap-3">
          <IterationsTimeline
            iterations={iterations}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          {canWrite && session.status !== 'completed' && session.status !== 'abandoned' ? (
            <form
              className="flex flex-col gap-2 rounded border border-border bg-background p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (newContent.trim()) appendM.mutate();
              }}
            >
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span>Kind</span>
                <select
                  value={newIterationKind}
                  onChange={(e) => setNewIterationKind(e.target.value as TrainingIterationKind)}
                  className="h-7 rounded border border-border bg-background px-2 text-[11px]"
                >
                  {(['agent_draft', 'tuner_correction', 'tuner_note', 'final'] as const).map(
                    (k) => (
                      <option key={k} value={k}>
                        {KIND_LABEL[k]}
                      </option>
                    ),
                  )}
                </select>
                <label className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    className="h-3 w-3"
                  />
                  Dry run
                </label>
              </label>
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Iteration content…"
                rows={4}
                className="font-mono text-[12px]"
              />
              <div className="flex justify-end">
                <Button type="submit" size="sm" disabled={!newContent.trim() || appendM.isPending}>
                  {appendM.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  ) : (
                    <Send className="h-3 w-3" aria-hidden />
                  )}
                  Append
                </Button>
              </div>
            </form>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          {selected ? (
            <>
              <header className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    Iteration #{selected.index + 1}
                  </span>
                  <span>{KIND_LABEL[selected.kind] ?? selected.kind}</span>
                  {selected.approved ? (
                    <span className="inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-success">
                      <Check className="h-2.5 w-2.5" aria-hidden /> Approved
                    </span>
                  ) : null}
                </div>
                {canWrite && !selected.approved ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => approveM.mutate(selected.id)}
                    disabled={approveM.isPending}
                  >
                    {approveM.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : (
                      <Plus className="h-3 w-3" aria-hidden />
                    )}
                    Approve & bump version
                  </Button>
                ) : null}
              </header>
              {prev ? (
                <DraftDiffViewer
                  before={prev.content}
                  after={selected.content}
                  beforeLabel={`#${prev.index + 1} ${KIND_LABEL[prev.kind] ?? prev.kind}`}
                  afterLabel={`#${selected.index + 1} ${KIND_LABEL[selected.kind] ?? selected.kind}`}
                />
              ) : (
                <div className="rounded border border-border bg-background p-3">
                  <h3 className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {KIND_LABEL[selected.kind] ?? selected.kind}
                  </h3>
                  <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded bg-muted/30 p-2 font-mono text-[12px] text-foreground/90">
                    {selected.content}
                  </pre>
                </div>
              )}
            </>
          ) : (
            <div className="rounded border border-border bg-background px-3 py-6 text-center text-[12px] text-muted-foreground/70">
              Select an iteration to see its diff.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function findPreviousIteration(
  iterations: TrainingIteration[],
  current: TrainingIteration,
): TrainingIteration | null {
  // Prefer the explicit prev_iteration_id when present; otherwise pick
  // the iteration immediately before this one by `index`.
  if (current.prevIterationId) {
    const explicit = iterations.find((it) => it.id === current.prevIterationId);
    if (explicit) return explicit;
  }
  const earlier = iterations.filter((it) => it.index < current.index);
  if (earlier.length === 0) return null;
  return earlier[earlier.length - 1] ?? null;
}

export const Route = createFileRoute('/_authenticated/university/training/$sessionId')({
  component: TrainingSessionDetailRoute,
});
